import React, { forwardRef } from "react";

import iconChest from "./chrome/icon-chest.webp";
import iconCompletedCard from "./chrome/icon-node-completed-card.svg";
import iconCompleted from "./chrome/icon-node-completed.svg";
import iconCurrent from "./chrome/icon-node-current.svg";
import iconLocked from "./chrome/icon-node-locked.svg";
import iconAudio from "./chrome/icon-node-tipo-audio.svg";
import iconAula from "./chrome/icon-node-tipo-aula.svg";
import iconEnvio from "./chrome/icon-node-tipo-envio.svg";
import iconEscrita from "./chrome/icon-node-tipo-escrita.svg";
import iconExercicio from "./chrome/icon-node-tipo-exercicio.svg";
import iconExplorar from "./chrome/icon-node-tipo-explorar.svg";
import iconImagem from "./chrome/icon-node-tipo-imagem.svg";
import iconJogo from "./chrome/icon-node-tipo-jogo.svg";
import iconLeitura from "./chrome/icon-node-tipo-leitura.svg";
import iconLink from "./chrome/icon-node-tipo-link.svg";
import iconMapa from "./chrome/icon-node-tipo-mapa.svg";
import iconVideo from "./chrome/icon-node-tipo-video.svg";
import seloCarta from "./chrome/icon-selo-carta.svg";
import seloConcluido from "./chrome/icon-selo-concluido.svg";

// `available` usa o mesmo ícone de `current` — visualmente é um nó liberado. O
// que distingue o atual é o avatar em cima e o halo pulsante (CSS).
const ICONE_POR_ESTADO = {
  locked: iconLocked,
  current: iconCurrent,
  available: iconCurrent,
  completed: iconCompleted
};

// Em que estados a fase de recompensa mostra o MEDALHÃO do mascote em vez do
// cadeado/bolinha. `completed` fica fora de propósito — depois de concluída, a
// informação relevante é "feito", não "tinha prêmio".
//
// A cabeça do mascote vem por PROP, e não de um import daqui. Havia um
// `import mascoteCabeca from "../assets/mascots/mascot-nordeste-cabeca.webp"`
// neste arquivo, e ele sobreviveu à virada multi-região de 05/08/2026 porque a
// varredura daquele dia procurou imports de `conteudo/` e não imports de ASSET de
// região. O sintoma foi exato: o tabuleiro da Amazônia, com as cartas da Amazônia,
// e o tatu-bola no medalhão da fase rara. Quem impede a volta disso é
// `portabilidade.test.js`.
const ESTADOS_COM_MEDALHAO = ["locked", "current", "available"];

// O pictograma do nó LIBERADO, por família de tipo — o que a fase é, em vez da
// estrela genérica que servia a todas. É signposting: o aluno decide se abre
// agora sabendo se vai assistir, ler ou responder.
//
// O disco e o anel verdes são os mesmos em todos: a COR continua dizendo só
// "pode entrar", e o miolo diz o quê. Duas linguagens de cor na mesma tela
// (estado e tipo) seria uma a mais do que a criança precisa aprender.
//
// Família fora desta tabela — ou `null`, que é o que o adaptador devolve para
// tipo que ele não conhece — cai na estrela de `ICONE_POR_ESTADO`. A tabela do
// AVA (`ava/atividadeParaTrilha.js`) explica por que errar o pictograma é pior
// que não ter.
const ICONE_POR_FAMILIA = {
  aula: iconAula,
  exercicio: iconExercicio,
  video: iconVideo,
  audio: iconAudio,
  leitura: iconLeitura,
  imagem: iconImagem,
  mapa: iconMapa,
  jogo: iconJogo,
  explorar: iconExplorar,
  escrita: iconEscrita,
  envio: iconEnvio,
  link: iconLink
};

// Como o leitor de tela chama cada família. Sem isto o tipo seria informação
// exclusiva de quem vê a tela — o mesmo raciocínio da promessa do baú.
const TEXTO_POR_FAMILIA = {
  aula: "aula",
  exercicio: "exercício",
  video: "vídeo",
  audio: "áudio",
  leitura: "leitura",
  imagem: "imagem",
  mapa: "mapa",
  jogo: "jogo",
  explorar: "simulador",
  escrita: "escrita",
  envio: "envio de arquivo",
  link: "link"
};

const TEXTO_POR_ESTADO = {
  locked: "bloqueada",
  current: "disponível agora",
  available: "disponível",
  completed: "concluída"
};

const NoTrilha = forwardRef(
  (
    {
      numero,
      titulo,
      estado,
      // A família do tipo ("aula", "exercicio", "video", "leitura"), do modelo
      // neutro. `undefined` é o caso normal de um host que não a informa.
      tipo,
      especial,
      // Esta fase ENTREGA CARTA a este aluno. Vem de `faseEntregaCarta`, que cruza a
      // regra com o sorteio — não é "esta fase é premiada". A distinção nasceu em
      // 24/08/2026, quando a carta rara passou a ser sorteada: aqui ela decide a
      // estrela dourada da fase concluída, e a estrela afirmaria uma conquista que
      // não houve.
      comCard,
      clicavel,
      esquerdaPct,
      topoPct,
      onAbrir,
      // A cabeça do mascote DESTA região, para o medalhão da fase rara. Sem ela o
      // nó cai no ícone normal do estado — a fase continua sendo o marco do fim da
      // leva (78px e anel ciano vêm do CSS, pela classe `--especial`), só não tem
      // retrato dentro.
      mascoteCabeca
    },
    ref
  ) => {
    // Fase que entregou card e já está concluída vira estrela dourada. Só depois
    // de concluída: `completed` não está em `ESTADOS_COM_MEDALHAO`, então a fase
    // rara passa do medalhão do mascote para a estrela no momento em que o aluno a
    // termina.
    //
    // E passa do medalhão para NADA quando a carta rara não foi sorteada: ali houve
    // a cena do arco, que não é colecionável. O marco do fim de leva fica (o
    // tratamento `--especial` não depende de `comCard`), o que muda é a estrela.
    const cardConquistado = comCard && estado === "completed";
    // O ÍCONE de medalhão depende do estado (não existe para `completed`), mas o
    // TRATAMENTO de fase especial não: uma fase rara concluída continua sendo o
    // marco do fim da leva, e continua em 72px com o anel ciano. Sem isso ela
    // ficava idêntica a uma fase de card comum concluída — as duas com a mesma
    // estrela dourada de 56px —, e o destino da leva deixava de se distinguir do
    // caminho até ele.
    const medalhao = Boolean(
      especial && mascoteCabeca && ESTADOS_COM_MEDALHAO.includes(estado)
    );

    // A PROMESSA: fase bloqueada que vai entregar carta troca o cadeado pelo BAÚ
    // (21/08/2026). É o contrário do rótulo, que só aparece DEPOIS de liberada, e
    // não é contradição — o que se esconde é o CONTEÚDO da fase, o que se mostra é
    // que ali tem prêmio. Uma coisa tira a descoberta, a outra é o que faz a
    // criança querer chegar lá.
    //
    // Um baú fechado É o cadeado dessa fase: diz "trancado" e "tem coisa dentro"
    // com um desenho só, então nada se perde na troca. Ele fica DENTRO do círculo,
    // e não colado na quina como um selo — foi a primeira tentativa, e sobreposto
    // ele cobria o próprio cadeado que devia complementar.
    //
    // É o MESMO baú da cerimônia de card (`icon-chest.webp`), de propósito: o que
    // a criança vê fechado na trilha é o que ela vê estourar em luz quando chega
    // lá. Um segundo desenho de baú faria dois objetos onde a história tem um.
    //
    // FORA DA FASE RARA (`especial`), decisão do Kilson: o medalhão do mascote já é
    // onde a carta rara é encontrada, e trocá-lo pelo baú apagaria o marco de fim
    // de leva. A regra é `especial`, e não `medalhao`, para a fase rara da região
    // sem mascote também continuar sendo o marco.
    //
    // Só em `locked`: liberada, o nó volta ao ícone do estado. A promessa serve
    // para quem ainda não chegou — de pé em cima dela, o aluno já vai abrir.
    //
    // O SORTEIO DA CARTA RARA NÃO VAZA POR AQUI, e foi conferido em 24/08/2026: o
    // `!especial` já exclui toda fase rara, então `comCard` — que agora depende do
    // sorteio — só é consultado em fase COMUM, onde a carta é garantida. O nó
    // bloqueado do fim de leva continua sendo o medalhão do mascote, igual para
    // quem vai ganhar a carta e para quem não vai. Se um dia o baú passar a valer
    // na fase rara, ele passa a anunciar o resultado antes da hora.
    const bauTrancado = comCard && !especial && estado === "locked";

    // O tipo aparece na fase LIBERADA **e na CONCLUÍDA** (decisão do Kilson em
    // 21/08/2026). Na bloqueada não: ali o nó já tem o que dizer — está trancado,
    // e tem ou não prêmio —, e é o mesmo critério do título.
    //
    // A primeira versão parava no `available`/`current`, e o efeito era que a
    // trilha terminada virava uma fileira de tiques iguais: no fim, quando o
    // aluno olha o percurso inteiro, ele perdia justamente a leitura do que
    // fez. Hoje o tique e a estrela saem do miolo e viram SELO de canto
    // (`seloDeEstado`, abaixo), então as duas informações cabem no mesmo nó: o
    // que a fase era, e que ela está feita.
    const iconeDoTipo = estado !== "locked" && ICONE_POR_FAMILIA[tipo];

    // A ORDEM É A HIERARQUIA do que o nó tem para dizer, e cada degrau já foi
    // discutido: o medalhão do mascote é o marco de fim de leva e vence tudo; o
    // baú é a promessa da carta na fase trancada; o tipo vem em seguida e vale de
    // `available` a `completed`; e os ícones de estado são o piso, para o host que
    // não informa tipo.
    //
    // `cardConquistado` saiu daqui e virou selo: com o tipo no miolo, a estrela
    // dourada de 56px não tinha mais lugar — e ela conta uma coisa (ganhei carta)
    // que não é a mesma que o miolo conta (que fase era).
    let icone = ICONE_POR_ESTADO[estado] || iconLocked;
    if (medalhao) {
      icone = mascoteCabeca;
    } else if (bauTrancado) {
      icone = iconChest;
    } else if (iconeDoTipo) {
      icone = iconeDoTipo;
    } else if (cardConquistado) {
      icone = iconCompletedCard;
    }

    // O SELO DE ESTADO, no canto do nó: só na fase concluída, e só quando o miolo
    // está ocupado pelo tipo. Sem tipo o miolo já é o tique (ou a estrela
    // dourada), e um selo ali repetiria o desenho ao lado dele mesmo.
    //
    // Estrela para a fase que entregou carta, tique para as outras. São dois
    // selos e não um porque, com a estrela fora do miolo, o tique sozinho
    // apagaria a diferença entre "fiz" e "fiz e ganhei carta" — e essa diferença
    // é metade do que o aluno vem reler no fim da trilha.
    let seloDeEstado = null;
    if (estado === "completed" && iconeDoTipo) {
      seloDeEstado = cardConquistado ? seloCarta : seloConcluido;
    }

    const descricaoEstado = TEXTO_POR_ESTADO[estado] || TEXTO_POR_ESTADO.locked;
    // O TÍTULO DA FASE SÓ APARECE DEPOIS DE LIBERADA (decisão do Kilson em
    // 21/08/2026). Fase bloqueada é o percurso que o aluno ainda não alcançou:
    // ler ali "Tipos de vistas" e "Mosaicos" entrega de antemão o que vem, e
    // desmancha a descoberta que é o motivo de a trilha existir.
    //
    // Não é só o rótulo visível: `title` (tooltip) e `aria-label` também perdem
    // o texto, e o `<span>` não é renderizado. Esconder por CSS deixaria o
    // título no DOM — visível no inspetor, lido por leitor de tela e achável
    // por Ctrl+F —, o que é "não aparecer" só para parte dos alunos.
    const mostraTitulo = estado !== "locked";
    // O selo é visual; a promessa também tem de chegar a quem usa leitor de tela,
    // e é aqui que ela chega. Um sufixo só, na mesma ordem de prioridade do
    // ícone: a fase rara continua anunciando "(fase de recompensa)", que é o
    // equivalente falado do medalhão, e não acumula com a promessa do baú.
    let promessa = "";
    if (cardConquistado) promessa = " (card conquistado)";
    else if (especial) promessa = " (fase de recompensa)";
    else if (bauTrancado) promessa = " (aqui você ganha uma carta)";

    // "Fase 3: Frações — aula, disponível agora". O tipo vem antes do estado
    // porque é o que o aluno quer saber para decidir abrir, e só é anunciado
    // quando o pictograma também aparece: rótulo e desenho contando coisas
    // diferentes é pior que os dois contarem menos.
    const nomeDoTipo = (iconeDoTipo && TEXTO_POR_FAMILIA[tipo]) || "";
    const rotuloAcessivel = `Fase ${numero}: ${
      mostraTitulo ? `${titulo} — ` : ""
    }${nomeDoTipo ? `${nomeDoTipo}, ` : ""}${descricaoEstado}${promessa}`;

    return (
      <button
        ref={ref}
        type="button"
        className={`trilha-ludica__no trilha-ludica__no--${estado}${
          especial ? " trilha-ludica__no--especial" : ""
        }${cardConquistado ? " trilha-ludica__no--card-concluido" : ""}${
          bauTrancado ? " trilha-ludica__no--bau" : ""
        }`}
        // Os DOIS eixos em porcentagem da faixa: `left` é a distância percorrida
        // e `top` a altura sobre a estrada. O CSS centra a caixa nos dois com
        // `translate(-50%, -50%)`, então as coordenadas marcam o CENTRO do nó.
        //
        // `left` em % e não em px porque a faixa ESTICA quando o percurso é mais
        // curto que a pista (`min-width: 100%`): em px os nós ficariam parados à
        // esquerda enquanto o cenário e o caminho SVG esticavam sem eles.
        style={{ left: `${esquerdaPct}%`, top: `${topoPct}%` }}
        disabled={!clicavel}
        aria-label={rotuloAcessivel}
        aria-current={estado === "current" ? "step" : undefined}
        title={mostraTitulo ? titulo : undefined}
        onClick={() => {
          if (clicavel) onAbrir();
        }}
      >
        <img className="trilha-ludica__no-icone" src={icone} alt="" />
        {/* Depois do ícone no DOM, para ficar por cima dele sem depender só do
            z-index. `alt=""` porque o `aria-label` do botão já diz "concluída" e
            "(card conquistado)" — o selo repetiria. */}
        {seloDeEstado && (
          <img
            className="trilha-ludica__selo-estado"
            src={seloDeEstado}
            alt=""
          />
        )}
        {mostraTitulo && (
          <span className="trilha-ludica__rotulo">{titulo}</span>
        )}
      </button>
    );
  }
);

export default NoTrilha;
